import React, { useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import * as z from "zod";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import { Mail, Lock, User, Phone, Gift, Eye, EyeOff } from "lucide-react";
import { PhoneInput } from 'react-international-phone';
import 'react-international-phone/style.css';
import '@/styles/phone-input-overrides.css';

import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { ImageWithFallback } from "@/components/common/ImageWithFallback";
import { toast } from "sonner";
import { resolveReferralCode, validateReferralCode } from "@/utils/referralUtils";
import RegistrationSuccessDialog from "@/components/auth/RegistrationSuccessDialog";
import { userAssignmentService } from "@/services/userAssignmentService";
import { GeolocationService } from "@/services/geolocationService";

const ENABLE_GOOGLE_AUTH = false;

const formSchema = z.object({
  name: z.string().min(2, { message: "Name must be at least 2 characters" }),
  email: z.string().email({ message: "Invalid email address" }),
  phone: z.string().min(10, { message: "Phone number must be at least 10 digits" }),
  password: z.string().min(6, { message: "Password must be at least 6 characters" }),
  referralCode: z.string().optional(),
  country: z.string().min(1, { message: "Country is required" }),
  countryCode: z.string().min(2, { message: "Country code is required" }),
});

const MobileRegisterForm: React.FC = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [detectedReferralCode, setDetectedReferralCode] = useState<string | null>(null);
  const [isReferralReadOnly, setIsReferralReadOnly] = useState(false);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState("");
  const [detectedCountry, setDetectedCountry] = useState<{ country: string; countryCode: string } | null>(null);

  React.useEffect(() => {
    const code = resolveReferralCode();
    if (code) {
      setDetectedReferralCode(code);
      setIsReferralReadOnly(true);
    }
  }, []);

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: "",
      email: "",
      phone: "",
      password: "",
      referralCode: "",
      country: "",
      countryCode: "",
    },
  });

  React.useEffect(() => {
    if (detectedReferralCode) {
      form.setValue('referralCode', detectedReferralCode);
    }
  }, [detectedReferralCode, form]);

  // Detect user's country on component mount
  React.useEffect(() => {
    const detectCountry = async () => {
      try {
        const countryData = await GeolocationService.detectCountry();
        setDetectedCountry(countryData);
        // Auto-set the country in the form
        form.setValue('country', countryData.country);
        form.setValue('countryCode', countryData.countryCode);
      } catch (error) {
        console.warn('Failed to detect country:', error);
        // Fallback to Vietnam
        setDetectedCountry({ country: 'Vietnam', countryCode: 'VN' });
        form.setValue('country', 'Vietnam');
        form.setValue('countryCode', 'VN');
      }
    };

    detectCountry();
  }, [form]);

  async function onSubmit(values: z.infer<typeof formSchema>) {
    setIsLoading(true);
    
    try {
      console.log("🚀 [REGISTRATION] Starting simplified registration with immediate referral assignment");
      
      // STEP 1: Validate referral code if provided
      let referralData = null;
      if (values.referralCode) {
        console.log("🔍 [REGISTRATION] Validating referral code:", values.referralCode);
        referralData = await validateReferralCode(values.referralCode);
        if (values.referralCode && !referralData.isValid) {
          console.warn("⚠️ [REGISTRATION] Invalid referral code, continuing without it");
          toast.error("Invalid referral code. Registration will continue without it.");
        } else if (referralData.isValid) {
          console.log("✅ [REGISTRATION] Valid referral code detected");
        }
      }
      
      // STEP 2: Register the user with Supabase
      console.log("📝 [REGISTRATION] Creating user account...");
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email: values.email,
        password: values.password,
        options: {
          data: {
            name: values.name,
            phone: values.phone,
            country: values.country,
            countryCode: values.countryCode,
            referralCode: referralData?.isValid ? values.referralCode : null,
            referredBy: referralData?.isValid ? referralData.affiliateId : null,
          },
        },
      });
      
      if (authError) {
        console.error("❌ [REGISTRATION] Supabase auth error:", authError);
        toast.error(authError.message);
        return;
      }
      
      if (!authData?.user) {
        console.error("❌ [REGISTRATION] No user data returned from Supabase");
        toast.error("Registration failed - no user data");
        return;
      }
      
      console.log("✅ [REGISTRATION] User account created successfully:", authData.user.id);
      
      // Save country data to user_profiles
      try {
        const { error: profileError } = await supabase
          .from('user_profiles')
          .upsert({
            user_id: authData.user.id,
            country: values.country,
            country_code: values.countryCode,
            display_name: values.name,
            display_email: values.email,
          });

        if (profileError) {
          console.warn('Failed to save country data to profile:', profileError);
        }
      } catch (error) {
        console.warn('Error saving profile data:', error);
      }
      
      // STEP 3: Process referral immediately if valid
      if (referralData?.isValid) {
        console.log("🎯 [REGISTRATION] Processing referral immediately for user:", authData.user.id);
        try {
          const assignmentSuccess = await userAssignmentService.assignUserToAffiliateOnRegistration(
            authData.user.id,
            referralData.affiliateId,
            values.referralCode
          );
          
          if (assignmentSuccess) {
            console.log("✅ [REGISTRATION] User successfully assigned to affiliate system");
            toast.success("Registration successful! You've been assigned to your referrer's affiliate program.");
          } else {
            console.error("❌ [REGISTRATION] Failed to assign user to affiliate system");
            toast.warning("Registration successful, but affiliate assignment failed. Please contact support.");
          }
        } catch (affiliateError) {
          console.error("❌ [REGISTRATION] Exception during affiliate assignment:", affiliateError);
          toast.warning("Registration successful, but affiliate assignment encountered an error.");
        }
      } else {
        console.log("ℹ️ [REGISTRATION] No valid referral code, skipping affiliate assignment");
      }
      
      // STEP 4: Show success message
      console.log("🎉 [REGISTRATION] Registration process completed");
      setRegisteredEmail(values.email);
      setShowSuccessDialog(true);
      
      // Check if email confirmation is required
      const needsEmailConfirmation = !authData.session;
      if (needsEmailConfirmation) {
        toast.success("Registration successful! Please check your email to confirm your account.", {
          duration: 5000
        });
      } else {
        toast.success("Registration successful! You can now sign in.");
      }
      
    } catch (error) {
      console.error("❌ [REGISTRATION] Unexpected error:", error);
      toast.error("An unexpected error occurred");
    } finally {
      setIsLoading(false);
    }
  }
  
  const handleGoogleSignUp = async () => {
    const currentRefCode = form.getValues('referralCode') || detectedReferralCode;
    if (currentRefCode) {
      localStorage.setItem('pendingReferralCode', currentRefCode);
    }
    
    setIsLoading(true);
    
    try {
      const redirectUrl = `${window.location.origin}/auth/callback`;
      
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: redirectUrl,
        }
      });
      
      if (error) {
        toast.error(error.message);
        console.error("Google OAuth error:", error);
      }
    } catch (error) {
      toast.error("Failed to connect with Google");
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen w-full items-center justify-center">
      <div className="w-full max-w-md px-4 py-8">
        <div className="relative z-10 overflow-hidden rounded-xl bg-black/80 backdrop-blur-xl shadow-xl border border-white/10 px-6 py-8">
          {/* Logo */}
          <div className="mb-6 flex justify-center">
            <div className="w-44 h-16 relative">
              <ImageWithFallback 
                src="https://lbvhywxvthwgiebjsrlr.supabase.co/storage/v1/object/public/logoexchangeplatformurl//logo%20trade%20bot%20365.png" 
                alt="Trade Bot 365 Logo" 
                className="w-full h-full object-contain"
                loading="eager"
                skeletonClassName="bg-muted/50"
              />
            </div>
          </div>
          
          {/* Header */}
          <div className="text-center mb-6">
            <h1 className="text-xl font-bold text-white mb-1">Create an account</h1>
            <p className="text-sm text-white/70">Join Trade Bot 365 today</p>
            {detectedReferralCode && (
              <div className="mt-2">
                <div className="inline-flex items-center px-3 py-1 rounded-full bg-botscheme-coral-orange/20 text-botscheme-coral-orange text-xs font-medium">
                  <Gift className="h-3 w-3 mr-1" /> Referral applied
                </div>
              </div>
            )}
          </div>
          
          {/* Form */}
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <div className="relative">
                      <User className="absolute left-3 top-2.5 h-4 w-4 text-white/50" />
                      <FormControl>
                        <Input 
                          placeholder="Full Name" 
                          className="pl-9 bg-white/10 border-white/10 text-white placeholder:text-white/50 focus:border-botscheme-ocean-blue h-10" 
                          {...field} 
                        />
                      </FormControl>
                    </div>
                    <FormMessage className="text-xs text-red-300" />
                  </FormItem>
                )}
              />
              
              <div className="grid grid-cols-1 gap-4">
                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <div className="relative">
                        <Mail className="absolute left-3 top-2.5 h-4 w-4 text-white/50" />
                        <FormControl>
                          <Input 
                            placeholder="Email Address" 
                            className="pl-9 bg-white/10 border-white/10 text-white placeholder:text-white/50 focus:border-botscheme-ocean-blue h-10" 
                            {...field} 
                          />
                        </FormControl>
                      </div>
                      <FormMessage className="text-xs text-red-300" />
                    </FormItem>
                  )}
                />
                
                <FormField
                  control={form.control}
                  name="phone"
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <PhoneInput
                          defaultCountry={detectedCountry?.countryCode?.toLowerCase() || 'vn'}
                          value={field.value}
                          onChange={(phone, meta) => {
                            field.onChange(phone);
                            if (meta.country) {
                              form.setValue('countryCode', meta.country.iso2.toUpperCase());
                              form.setValue('country', meta.country.name);
                            }
                          }}
                          inputClassName="bg-white/10 border-white/10 text-white placeholder:text-white/50 focus:border-botscheme-ocean-blue h-10"
                          countrySelectorStyleProps={{
                            buttonClassName: "bg-white/10 border-white/10 hover:bg-white/20",
                          }}
                          placeholder="Phone Number"
                        />
                      </FormControl>
                      <FormMessage className="text-xs text-red-300" />
                    </FormItem>
                  )}
                />
              </div>
              
              <FormField
                control={form.control}
                name="password"
                render={({ field }) => (
                  <FormItem>
                    <div className="relative">
                      <Lock className="absolute left-3 top-2.5 h-4 w-4 text-white/50" />
                      <FormControl>
                        <Input 
                          type={showPassword ? "text" : "password"}
                          placeholder="Password" 
                          className="pl-9 bg-white/10 border-white/10 text-white placeholder:text-white/50 focus:border-botscheme-ocean-blue h-10" 
                          {...field} 
                        />
                      </FormControl>
                      <button 
                        type="button" 
                        className="absolute right-3 top-2.5 text-white/50"
                        onClick={() => setShowPassword(!showPassword)}
                      >
                        {showPassword ? (
                          <EyeOff className="h-4 w-4" />
                        ) : (
                          <Eye className="h-4 w-4" />
                        )}
                      </button>
                    </div>
                    <FormMessage className="text-xs text-red-300" />
                  </FormItem>
                )}
              />
              
              <FormField
                control={form.control}
                name="referralCode"
                render={({ field }) => (
                  <FormItem>
                    <div className="relative">
                      <Gift className="absolute left-3 top-2.5 h-4 w-4 text-white/50" />
                      <FormControl>
                        <Input 
                          placeholder={isReferralReadOnly ? "" : "Referral Code (Optional)"}
                          readOnly={isReferralReadOnly}
                          className={`pl-9 bg-white/10 border-white/10 text-white placeholder:text-white/50 focus:border-botscheme-ocean-blue h-10 ${
                            isReferralReadOnly ? "opacity-80" : ""
                          }`}
                          {...field} 
                        />
                      </FormControl>
                    </div>
                    <FormMessage className="text-xs text-red-300" />
                  </FormItem>
                )}
              />
              
              <Button 
                type="submit" 
                className="w-full bg-gradient-to-r from-botscheme-ocean-blue to-botscheme-emerald-green hover:opacity-90 transition-all h-11 mt-2" 
                disabled={isLoading}
              >
                {isLoading ? (
                  <span className="flex items-center">
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Creating account...
                  </span>
                ) : "Create Account"}
              </Button>
            </form>
          </Form>
          
          {/* Google OAuth Section - Hidden based on flag */}
          {ENABLE_GOOGLE_AUTH && (
            <>
              <div className="relative my-6">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-white/10"></div>
                </div>
                <div className="relative flex justify-center text-xs">
                  <span className="bg-black/80 px-2 text-white/50">Or continue with</span>
                </div>
              </div>
              
              <Button 
                variant="outline" 
                className="w-full border-white/10 bg-white/5 text-white hover:bg-white/10 transition-all h-11" 
                onClick={handleGoogleSignUp}
                disabled={isLoading}
              >
                <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24">
                  <path
                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                    fill="#4285F4"
                  />
                  <path
                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                    fill="#34A853"
                  />
                  <path
                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                    fill="#FBBC05"
                  />
                  <path
                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                    fill="#EA4335"
                  />
                </svg>
                Sign up with Google
              </Button>
            </>
          )}
          
          <div className="text-center mt-6">
            <Button 
              variant="link" 
              className="text-botscheme-ocean-blue hover:text-botscheme-royal-purple transition-colors text-sm p-0"
              onClick={() => navigate("/auth/login")}
            >
              Already have an account? Sign in
            </Button>
          </div>
          
          {/* Decorative background elements */}
          <div className="absolute -z-10 top-10 right-10 w-20 h-20 rounded-full bg-botscheme-royal-purple/20 blur-xl"></div>
          <div className="absolute -z-10 bottom-10 left-10 w-24 h-24 rounded-full bg-botscheme-ocean-blue/20 blur-xl"></div>
        </div>
      </div>
      
      {/* Success Dialog */}
      <RegistrationSuccessDialog 
        open={showSuccessDialog}
        onOpenChange={setShowSuccessDialog}
        email={registeredEmail}
      />
      
      {/* Full screen background gradient */}
      <div className="fixed inset-0 -z-20 bg-gradient-to-br from-black via-botscheme-royal-purple/10 to-botscheme-emerald-green/10"></div>
    </div>
  );
};

export default MobileRegisterForm;
